import { HumeClient } from "hume";
import { sttTtsApi } from "./services";

interface HumeTokenResponse {
  token: string;
  // Add other expected fields if any
}

let humeClient: HumeClient | null = null;
let accessToken: "";
let initializationError: string | null = null;

// Custom error class for TTS errors
export class TTSError extends Error {
  constructor(message: string, public originalError?: any) {
    super(message);
    this.name = "TTSError";
  }
}

// Initialize the Hume client with a token from the backend
async function getHumeClient(): Promise<HumeClient> {
  // If we already have an error, don't try again
  if (initializationError) {
    throw new TTSError(initializationError);
  }

  if (!humeClient) {
    try {
      // Get the Hume token from our backend
      const tokenResponse = await sttTtsApi.getHumeToken();
      console.log("Hume token response:", tokenResponse);

      if (
        tokenResponse.kind === "ok" &&
        (tokenResponse as any).data?.access_token
      ) {
        accessToken = (tokenResponse as any).data.access_token;
        console.log("Using token:", accessToken);
        humeClient = new HumeClient({
          accessToken,
        });
        console.log("Hume client initialized successfully", humeClient);
      } else {
        const errorMessage = "Failed to get Hume API token";
        console.error("Failed to get Hume API token:", tokenResponse);
        initializationError = errorMessage;
        throw new TTSError(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to initialize TTS service";
      console.error("Error initializing Hume client:", error);
      initializationError = errorMessage;
      throw new TTSError(errorMessage, error);
    }
  }

  return humeClient;
}

/**
 * Generate speech audio from plain text using Hume TTS.
 * Returns base-64 encoded WAV audio which can be passed directly to an
 * <audio> element or Audio constructor as a data-URI.
 * @throws {TTSError} When there's an error with the TTS service
 */
export async function synthesizeSpeech(text: string): Promise<string> {
  if (!text || text.trim().length === 0) {
    throw new TTSError("No text provided to synthesize");
  }

  try {
    const client = await getHumeClient();

    if (!client) {
      throw new TTSError("TTS service is not available");
    }

    const response = await client.tts.synthesizeJson({
      body: {
        utterances: [
          {
            description:
              "A warm, expressive, and nurturing female voice with a gentle, refined British accent (RP). The tone is comforting and wise, yet consistently cheerful and inspiring—like a beloved elderly teacher reading a captivating story. She speaks with soft articulation, occasional playful lifts in pitch to convey enthusiasm, and a rhythmic, motherly cadence. The overall effect is sophisticated but deeply approachable, avoiding any harshness or stiffness.",
            text,
          },
        ],
        // instantMode: true,
      },
      accessToken,
    });

    const audioB64 = response.generations?.[0]?.audio;
    if (!audioB64) {
      throw new Error("Hume TTS response did not contain audio data");
    }

    return audioB64;
  } catch (error) {
    console.error("Error in synthesizeSpeech:", error);
    if (error instanceof TTSError) {
      throw error; // Re-throw our custom error
    }
    // Wrap any other errors in our custom error type
    const errorMessage =
      error instanceof Error ? error.message : "Failed to generate speech";
    throw new TTSError(errorMessage, error);
    // Reset the client if there's an error to get a new token on next attempt
    humeClient = null;
  }
}

export default {
  synthesizeSpeech,
};
